import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  downloadSurveyExport,
  useSurvey,
  useSurveyAggregate,
  useSurveyFunnel,
  useSurveyResponses,
  type SurveyCell,
  type SurveyQuestionAggregate,
} from '../../api/surveys.api';
import { Btn, Icon, ICONS } from '../../features/forms/ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../../features/forms/ui/tokens';

/**
 * Survey results — Spreadsheet (responses with the three Skipped states) +
 * Summary (per-question aggregation + page funnel), with CSV/PDF export.
 * Backend endpoints: /surveys/:id/{responses,aggregate,funnel,export.*}.
 */
type Tab = 'spreadsheet' | 'summary';

export function SurveyResultsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: detail } = useSurvey(id);
  const [tab, setTab] = useState<Tab>('spreadsheet');
  const [exporting, setExporting] = useState<'csv' | 'pdf' | null>(null);

  const name = detail?.survey.name ?? 'Survey';

  const doExport = async (format: 'csv' | 'pdf') => {
    if (!id) return;
    setExporting(format);
    try {
      await downloadSurveyExport(id, format);
    } catch {
      /* surfaced via the disabled state resetting; a toast layer can hook here */
    } finally {
      setExporting(null);
    }
  };

  return (
    <div style={{ padding: '26px 30px', fontFamily: FORM_UI_FONT, color: C.ink }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <button
            type="button"
            onClick={() => navigate(`/surveys/${id}`)}
            style={{ fontSize: 12, color: C.faint, fontWeight: 500, background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: FORM_UI_FONT, padding: 0 }}
          >
            Engage › Surveys › {name} ›
          </button>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: C.indigo, margin: '4px 0 4px' }}>Results</h1>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn kind="ghost" disabled={exporting !== null} onClick={() => doExport('csv')}>
            <Icon d={ICONS.doc} size={15} color={C.ink} />
            {exporting === 'csv' ? 'Exporting…' : 'CSV'}
          </Btn>
          <Btn kind="ghost" disabled={exporting !== null} onClick={() => doExport('pdf')}>
            <Icon d={ICONS.doc} size={15} color={C.ink} />
            {exporting === 'pdf' ? 'Exporting…' : 'PDF'}
          </Btn>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, margin: '18px 0 16px', borderBottom: `1px solid ${C.line}` }}>
        <TabBtn label="Spreadsheet" active={tab === 'spreadsheet'} onClick={() => setTab('spreadsheet')} />
        <TabBtn label="Summary" active={tab === 'summary'} onClick={() => setTab('summary')} />
      </div>

      {tab === 'spreadsheet' ? <SpreadsheetTab id={id} /> : <SummaryTab id={id} />}
    </div>
  );
}

// ─── Spreadsheet ────────────────────────────────────────────────────────────

function SpreadsheetTab({ id }: { id: string | undefined }) {
  const { data, isLoading } = useSurveyResponses(id);

  if (isLoading) return <Loading />;
  if (!data || data.rows.length === 0) {
    return <Empty title="No responses yet" body="Completed responses will appear here as a spreadsheet, one row per respondent." />;
  }

  return (
    <div>
      {data.capped && (
        <div style={{ fontSize: 12, color: C.warn, background: C.warnBg, borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
          Showing the most recent {data.total} responses. Export to CSV for the full set.
        </div>
      )}
      <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, border: `1px solid ${C.line}` }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
          <thead>
            <tr>
              <Th>Completed</Th>
              <Th>Source</Th>
              {data.columns.map((c) => (
                <Th key={c.key}>{c.label}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.id} style={{ borderTop: `1px solid ${C.line}` }}>
                <Td>{row.completed_at ? new Date(row.completed_at).toLocaleString() : '-'}</Td>
                <Td>{row.source}</Td>
                {data.columns.map((c) => (
                  <CellTd key={c.key} cell={row.cells[c.key]} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Legend />
    </div>
  );
}

function CellTd({ cell }: { cell: SurveyCell | undefined }) {
  if (!cell || cell.state === 'value') {
    return <Td>{cell && cell.state === 'value' ? cell.value : ''}</Td>;
  }
  if (cell.state === 'skipped_optional') {
    return (
      <td style={{ ...tdStyle, color: C.faint, fontStyle: 'italic', background: C.bg }} title="Presented but left blank">
        Skipped
      </td>
    );
  }
  // skipped_not_shown — hatched
  return (
    <td
      style={{
        ...tdStyle,
        color: C.faint,
        fontStyle: 'italic',
        backgroundImage: `repeating-linear-gradient(45deg, ${C.line} 0, ${C.line} 1px, transparent 1px, transparent 6px)`,
      }}
      title="Never shown to this respondent (branched away)"
    >
      Not shown
    </td>
  );
}

function Legend() {
  return (
    <div style={{ display: 'flex', gap: 18, marginTop: 12, fontSize: 11.5, color: C.sub }}>
      <span><Swatch bg={C.bg} /> <i>Skipped</i>: presented but left blank</span>
      <span>
        <Swatch bg="transparent" hatched /> <i>Not shown</i>: branched away, or the page was never seen
      </span>
    </div>
  );
}

function Swatch({ bg, hatched }: { bg: string; hatched?: boolean }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 14,
        height: 14,
        verticalAlign: 'middle',
        marginRight: 4,
        borderRadius: 3,
        border: `1px solid ${C.line}`,
        background: bg,
        backgroundImage: hatched ? `repeating-linear-gradient(45deg, ${C.line} 0, ${C.line} 1px, transparent 1px, transparent 5px)` : undefined,
      }}
    />
  );
}

// ─── Summary ────────────────────────────────────────────────────────────────

function SummaryTab({ id }: { id: string | undefined }) {
  const { data: agg, isLoading } = useSurveyAggregate(id);
  const { data: funnel } = useSurveyFunnel(id);

  if (isLoading) return <Loading />;
  if (!agg || agg.total_responses === 0) {
    return <Empty title="No responses to summarise" body="Per-question charts and the page funnel appear once responses arrive." />;
  }

  return (
    <div>
      <div style={{ fontSize: 13, color: C.sub, marginBottom: 14 }}>
        Based on <strong style={{ color: C.indigo }}>{agg.total_responses.toLocaleString()}</strong> completed{' '}
        {agg.total_responses === 1 ? 'response' : 'responses'}.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
        {agg.questions.map((q) => (
          <QuestionCard key={q.key} q={q} />
        ))}
      </div>

      {funnel && funnel.length > 0 && (
        <div style={{ marginTop: 26 }}>
          <h2 style={{ fontSize: 15, fontWeight: 800, color: C.indigo, margin: '0 0 12px' }}>Page funnel</h2>
          <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.line}`, padding: '16px 18px' }}>
            {funnel.map((f, i) => (
              <div key={f.page_id} style={{ marginBottom: i < funnel.length - 1 ? 12 : 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600 }}>Page {i + 1}</span>
                  <span style={{ color: C.sub }}>
                    {f.views} viewed · {f.completed} completed · {Math.round(f.completion_rate * 100)}%
                  </span>
                </div>
                <Bar pct={f.completion_rate * 100} />
              </div>
            ))}
            <div style={{ fontSize: 11, color: C.faint, marginTop: 12 }}>
              Funnel uses page analytics events; it fills in as responses are collected.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function QuestionCard({ q }: { q: SurveyQuestionAggregate }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.line}`, padding: '16px 18px' }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: C.ink, marginBottom: 2 }}>{q.label}</div>
      <div style={{ fontSize: 11, color: C.faint, marginBottom: 12 }}>
        {q.type.replace('_', ' ')} · {q.answered} answered
      </div>
      <QuestionBody q={q} />
    </div>
  );
}

function QuestionBody({ q }: { q: SurveyQuestionAggregate }) {
  switch (q.type) {
    case 'NUMERIC_SCALE':
    case 'LIKERT':
      return (
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.indigo }}>
            {q.mean !== null ? q.mean.toFixed(2) : '-'} <span style={{ fontSize: 12, fontWeight: 600, color: C.faint }}>mean</span>
          </div>
          <Histogram histogram={q.histogram} />
        </div>
      );
    case 'NPS':
      return (
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: npsColor(q.score) }}>
            {q.score !== null ? q.score : '-'} <span style={{ fontSize: 12, fontWeight: 600, color: C.faint }}>NPS</span>
          </div>
          <div style={{ fontSize: 11.5, color: C.sub, margin: '4px 0 10px' }}>
            {q.promoters} promoters · {q.passives} passives · {q.detractors} detractors
          </div>
          <Histogram histogram={q.histogram} />
        </div>
      );
    case 'SINGLE_SELECT':
    case 'MULTI_SELECT':
      return (
        <div>
          {q.type === 'MULTI_SELECT' && (
            <div style={{ fontSize: 10.5, color: C.faint, marginBottom: 8 }}>Multi-select: percentages can total over 100%.</div>
          )}
          {q.options.map((o) => (
            <div key={o.value} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginBottom: 3 }}>
                <span>{o.value}</span>
                <span style={{ color: C.sub }}>
                  {o.count} · {o.pct}%
                </span>
              </div>
              <Bar pct={o.pct} />
            </div>
          ))}
        </div>
      );
    case 'TEXT':
      return <div style={{ fontSize: 13, color: C.sub }}>{q.answered} free-text answers: see the spreadsheet or CSV export.</div>;
  }
}

function Histogram({ histogram }: { histogram: Record<string, number> }) {
  const entries = Object.entries(histogram).sort((a, b) => Number(a[0]) - Number(b[0]));
  const max = Math.max(1, ...entries.map(([, n]) => n));
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 70, marginTop: 6 }}>
      {entries.map(([k, n]) => (
        <div key={k} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }} title={`${k}: ${n}`}>
          <div style={{ width: '100%', height: `${(n / max) * 52}px`, minHeight: n > 0 ? 3 : 0, background: C.indigo, borderRadius: '3px 3px 0 0' }} />
          <span style={{ fontSize: 9.5, color: C.faint }}>{k}</span>
        </div>
      ))}
    </div>
  );
}

function Bar({ pct }: { pct: number }) {
  return (
    <div style={{ height: 8, borderRadius: 999, background: C.bg, overflow: 'hidden' }}>
      <div style={{ width: `${Math.min(100, Math.max(0, pct))}%`, height: '100%', background: C.teal }} />
    </div>
  );
}

function npsColor(score: number | null): string {
  if (score === null) return C.faint;
  if (score >= 50) return C.tealDark;
  if (score >= 0) return C.warn;
  return C.danger;
}

// ─── shared bits ────────────────────────────────────────────────────────────

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '11px 14px',
  fontSize: 11,
  fontWeight: 700,
  color: C.faint,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  borderBottom: `1px solid ${C.line}`,
  background: '#fff',
  position: 'sticky',
  top: 0,
};
const tdStyle: React.CSSProperties = { padding: '10px 14px', color: C.ink, whiteSpace: 'nowrap', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' };

function Th({ children }: { children: React.ReactNode }) {
  return <th style={thStyle}>{children}</th>;
}
function Td({ children }: { children: React.ReactNode }) {
  return <td style={tdStyle}>{children}</td>;
}

function TabBtn({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontFamily: FORM_UI_FONT,
        fontWeight: 600,
        fontSize: 13,
        cursor: 'pointer',
        padding: '10px 16px',
        border: 'none',
        background: 'transparent',
        color: active ? C.indigo : C.sub,
        borderBottom: `2px solid ${active ? C.indigo : 'transparent'}`,
      }}
    >
      {label}
    </button>
  );
}

function Loading() {
  return <div style={{ padding: 40, textAlign: 'center', color: C.sub, fontFamily: FORM_UI_FONT }}>Loading…</div>;
}

function Empty({ title, body }: { title: string; body: string }) {
  return (
    <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${C.line}`, padding: '40px 28px', textAlign: 'center' }}>
      <Icon d={ICONS.layers} size={28} color={C.faint} />
      <div style={{ fontSize: 15, fontWeight: 700, color: C.indigoSoft, marginTop: 12 }}>{title}</div>
      <div style={{ fontSize: 12.5, color: C.sub, marginTop: 6, maxWidth: 460, marginInline: 'auto' }}>{body}</div>
    </div>
  );
}
